import { useQuery } from "@apollo/client";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { Box, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { TASKS_COUNT_QUERY } from "components/Tasks/schema";
import { DateTime } from "luxon";
import { Link, useParams } from "react-router-dom";

function StatRow({ label, count, loading }: { label: string; count: number; loading: boolean }) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={1}>
      <Box sx={{ width: 40, textAlign: "right" }}>
        {loading ? (
          <Skeleton variant="text" width={20} sx={{ ml: "auto" }} />
        ) : (
          <Typography variant="body1" color="text.primary" fontWeight="bold">
            {count}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

export const TasksSnapshot: React.FC = () => {
  const { tenantId, facilityId } = useParams<{ tenantId: string; facilityId: string }>();

  const baseSearch = `tenantId:${tenantId} facilityId:${facilityId}`;
  const fourteenDaysFromNow = DateTime.local().plus({ days: 14 }).toISODate();

  const { data: overdueData, loading: overdueLoading } = useQuery(TASKS_COUNT_QUERY, {
    variables: { search: `${baseSearch} status:OVERDUE` },
    fetchPolicy: "cache-and-network",
  });

  const { data: dueSoonData, loading: dueSoonLoading } = useQuery(TASKS_COUNT_QUERY, {
    variables: { search: `${baseSearch} status:ACTIVE before:${fourteenDaysFromNow}` },
    fetchPolicy: "cache-and-network",
  });

  return (
    <Paper elevation={0} sx={{ p: 2 }}>
      <Box sx={{ width: "256px", flexShrink: 0 }}>
        <Box
          component={Link}
          to={`/o/${tenantId}/facilities/${facilityId}/tasks`}
          sx={{
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            color: "text.primary",
            mb: 3,
          }}
        >
          <Typography variant="h6" sx={{ flex: 1 }}>
            📋 Tasks Snapshot
          </Typography>
          <ChevronRight sx={{ color: "action.active" }} />
        </Box>

        <Stack spacing={2}>
          <StatRow
            label="Overdue"
            count={overdueData?.virtualEvents.count ?? 0}
            loading={overdueLoading}
          />
          <StatRow
            label="Due Within 14 Days"
            count={dueSoonData?.virtualEvents.count ?? 0}
            loading={dueSoonLoading}
          />
        </Stack>
      </Box>
    </Paper>
  );
};
